import { useState } from "react";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import DsbpCommonPopup from "../../../../DSBP/DsbpCommonPopup";
import {
  deleteDashboardView,
  createOrUpdateDashboardModuleView,
} from "../../../../../apis/dashboardApi";
import {
  buildDashboardModule,
  buildDashboardPayload,
} from "../../../CreateViewDialog/dashboardPayloadConstants";

import { SUCCESS_TOAST_LIFE, ERROR_TOAST_LIFE } from "../../../../../constants/dashboardConstants";
import "./ViewHeader.scss";

import { useSelector } from "react-redux";

const ViewHeader = ({
  item,
  toast,
  onMaximize,
  onSettings,
  onTitleSave,
  onDelete,
  onDeleteSuccess,
  isMaximized = false,
}) => {
  const [currentTitle, setCurrentTitle] = useState(item.title);
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [editedTitle, setEditedTitle] = useState(item.title);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [savingTitle, setSavingTitle] = useState(false);
  const { userInformation } = useSelector((state) => state.UserReducer);

  const handleEditClick = (e) => {
    setIsEditingTitle(true);
    setEditedTitle(currentTitle);
  };

  const handleSaveClick = async (e) => {
    if (!editedTitle.trim()) return;

    // Check if edited title is the same as current title
    if (editedTitle.trim() === currentTitle) {
      setIsEditingTitle(false);
      toast?.current?.show?.({
        severity: "info",
        summary: "No Changes",
        detail: "Title has not been changed.",
        life: SUCCESS_TOAST_LIFE,
      });
      return;
    }

    // Validation: check for duplicate module names (case-insensitive, excluding current module)
    if (Array.isArray(item.allModuleNames)) {
      const newTitle = editedTitle.trim().toLowerCase();
      const currentId = item.moduleId;
      const duplicate = item.allModuleNames.some(
        (mod) => mod.moduleId !== currentId && mod.moduleName.trim().toLowerCase() === newTitle,
      );
      if (duplicate) {
        toast?.current?.show?.({
          severity: "error",
          summary: "Duplicate Name",
          detail: `A module with the name "${editedTitle.trim()}" already exists. Please choose a different name.`,
          life: ERROR_TOAST_LIFE,
        });
        return;
      }
    }
    setSavingTitle(true);
    const capitalize = (str) => str && str[0].toUpperCase() + str.slice(1).toLowerCase();
    try {
      const module = buildDashboardModule({
        moduleId: item.moduleId,
        moduleName: editedTitle.trim(),
        moduleType: capitalize(item.type),
        userId: userInformation?.userid || "",
        config: item.settings,
        Sequence: item.Sequence
      });
      const payload = buildDashboardPayload({ module });
      await createOrUpdateDashboardModuleView(payload);
      setCurrentTitle(editedTitle.trim());
      onTitleSave?.(editedTitle.trim());
      setIsEditingTitle(false);
      toast?.current?.show?.({
        severity: "success",
        summary: "Success",
        detail: "Title updated successfully",
        life: SUCCESS_TOAST_LIFE,
      });
    } catch (error) {
      toast?.current?.show?.({
        severity: "error",
        summary: "Error",
        detail: "Failed to update title. Please try again.",
        life: ERROR_TOAST_LIFE,
      });
    } finally {
      setSavingTitle(false);
    }
  };

  const handleTitleChange = (e) => {
    e.stopPropagation();
    const newValue = e.target.value;
    setEditedTitle(newValue);
  };

  const getTooltipMessage = () => {
    if (editedTitle.length === 40) {
      return "Maximum character limit reached (40/40)";
    }
    return "";
  };

  const handleKeyPress = (e) => {
    e.stopPropagation();
    if (e.key === "Enter") {
      e.preventDefault();
      handleSaveClick(e);
    } else if (e.key === "Escape") {
      e.preventDefault();
      setIsEditingTitle(false);
      setEditedTitle(currentTitle);
    }
  };

  const handleHeaderClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDeleteClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setShowDeleteConfirm(true);
  };

  const handleConfirmDelete = async () => {
    if (!item.moduleId) {
      toast?.current?.show({
        severity: "error",
        summary: "Error",
        detail: "Cannot delete view: Invalid module ID",
        life: ERROR_TOAST_LIFE,
      });
      setShowDeleteConfirm(false);
      return;
    }

    setDeleting(true);
    try {
      await deleteDashboardView(item.moduleId);
      toast?.current?.show({
        severity: "success",
        summary: "Success",
        detail: `"${currentTitle}" deleted successfully`,
        life: SUCCESS_TOAST_LIFE,
      });
      setShowDeleteConfirm(false);
      // Refresh dashboard data
      onDeleteSuccess?.();
      // Also call onDelete callback if provided
      onDelete?.();
    } catch (error) {
      console.error("Error deleting view:", error);
      toast?.current?.show({
        severity: "error",
        summary: "Error",
        detail: `Failed to delete "${currentTitle}". Please try again.`,
        life: ERROR_TOAST_LIFE,
      });
      setShowDeleteConfirm(false);
    } finally {
      setDeleting(false);
    }
  };

  const handleCancelDelete = () => {
    setShowDeleteConfirm(false);
  };

  return (
    <div className="view-item-header" onClick={handleHeaderClick}>
      <div className="title-section">
        {isEditingTitle ? (
          <>
            <InputText
              value={editedTitle}
              onChange={handleTitleChange}
              onKeyDown={handleKeyPress}
              className="title-input"
              maxLength={40}
              autoFocus
              tooltip={getTooltipMessage()}
              tooltipOptions={{
                position: "bottom",
                showDelay: 0,
                hideDelay: 300,
                event: "focus",
              }}
              disabled={savingTitle}
            />
            <Button
              icon={savingTitle ? "pi pi-spin pi-spinner" : "pi pi-check"}
              className="p-button-text p-button-sm title-action-btn title-action-btn-save"
              tooltip={savingTitle ? "Saving..." : "Save"}
              tooltipOptions={{ position: "bottom" }}
              onClick={handleSaveClick}
              disabled={savingTitle}
            />
            <Button
              icon="pi pi-times"
              className="p-button-text p-button-sm title-action-btn title-action-btn-cancel"
              tooltip="Cancel"
              tooltipOptions={{ position: "bottom" }}
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                setIsEditingTitle(false);
                setEditedTitle(currentTitle);
              }}
              disabled={savingTitle}
            />
          </>
        ) : (
          <>
            <h5>{currentTitle}</h5>
            <Button
              icon="pi pi-pencil"
              className="p-button-text p-button-sm title-action-btn"
              tooltip="Edit"
              tooltipOptions={{ position: "bottom" }}
              onClick={handleEditClick}
            />
          </>
        )}
      </div>
      <div className="header-actions">
        {Array.isArray(item?.data) && item.data.length > 0 && (
          <Button
            icon={isMaximized ? "pi pi-window-minimize" : "pi pi-window-maximize"}
            className="p-button-text p-button-sm header-icon-btn"
            tooltip={isMaximized ? "Minimize" : "Maximize"}
            tooltipOptions={{ position: "bottom" }}
            onClick={(e) => {
              e.stopPropagation();
              onMaximize?.();
            }}
          />
        )}
        <Button
          icon="pi pi-cog"
          className="p-button-text p-button-sm header-icon-btn"
          tooltip="Settings"
          tooltipOptions={{ position: "bottom" }}
          onClick={(e) => {
            e.stopPropagation();
            onSettings?.();
          }}
        />
        <Button
          icon="pi pi-trash"
          className="p-button-text p-button-sm p-button-danger header-icon-btn"
          tooltip="Delete"
          tooltipOptions={{ position: "bottom" }}
          onClick={handleDeleteClick}
        />
      </div>
      <DsbpCommonPopup
        actionHeader="Confirm Deletion"
        dasbpDialog={showDeleteConfirm}
        setDasbpDialog={setShowDeleteConfirm}
        rejectFormData={[{}]}
        onSubmit={handleConfirmDelete}
        deleteButtonShow={true}
        showCancel={true}
        okButtonShow={false}
        yesButtonShow={true}
        submitButtonShow={false}
        disconnectButtonShow={true}
      >
        <>
          Are you sure you want to delete <strong>"{currentTitle}" </strong>? This action cannot be
          undone.
        </>
      </DsbpCommonPopup>
    </div>
  );
};

export default ViewHeader;
